import { useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@cs2/contracts/transport";
import type { Utility } from "@cs2/contracts";
import { isPlacedUtility } from "@cs2/physics";
import {
  Play,
  Pause,
  Download,
  ChevronDown,
  CloudFog,
  CloudOff,
  Flag,
  History,
  Radio,
  RefreshCw,
  Shield,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOptionalNotification } from "../../../shared/ui/index.js";
import { usePublicPayload } from "../../../shared/api/usePayload.js";
import { AmbiguityPanel } from "./AmbiguityPanel.js";
import { useLiveSession } from "../../../shared/live/useLiveSession.js";
import { useQuickviewSession } from "./useQuickviewSession.js";
import { MembersDialog } from "../../../shared/members/MembersDialog.js";
import { getSession, logout } from "../../../shared/api/client.js";
import type Konva from "konva";
import { MapView } from "../../../shared/map/MapView.js";
import { UtilityDetailDialog } from "../../../shared/utility/UtilityDetailDialog.js";
import { exportTacticImage } from "./export-image.js";
import { buildPlayback } from "../model/playback.js";
import { usePlayback } from "../model/usePlayback.js";
import { QuickviewDetail } from "./QuickviewDetail.js";
import { useLiveFollow } from "../model/useLiveFollow.js";
import { useRevisionBanner } from "../model/useRevisionBanner.js";
import {
  QUICKVIEW_STORAGE_KEY,
  readQuickviewSelection,
} from "../../../shared/lib/quickview-storage.js";
import {
  findOrFirst,
  resolveAssignedUtilities,
  resolveAssignment,
} from "../../../shared/lib/tactic-selectors.js";
import { SnapshotsDialog } from "../../../shared/publishing/SnapshotsDialog.js";
import { PageState } from "../../../shared/layout/PageState.js";
import { AppShell } from "../../../shared/layout/AppShell.js";
import "./quickview.css";

const storageKey = QUICKVIEW_STORAGE_KEY;
const slots = ["P1", "P2", "P3", "P4", "P5"] as const;

function formatRelativeUpdate(updatedAt: string | number, now: number): string {
  const minutes = Math.max(
    0,
    Math.floor(
      (now -
        (typeof updatedAt === "number"
          ? updatedAt
          : new Date(updatedAt).getTime())) /
        60_000,
    ),
  );
  if (minutes === 0) return "GERADE EBEN";
  return `VOR ${minutes} MIN`;
}

function QuickviewStatus({
  session,
  icon: Icon,
  message,
}: {
  session: Session | null;
  icon: typeof CloudFog;
  message: string;
}) {
  return (
    <PageState active="quickview" session={session}>
      <Icon aria-hidden="true" />
      <p>{message}</p>
    </PageState>
  );
}

export function QuickviewPage() {
  const navigate = useNavigate();
  const {
    session,
    sessionRef,
    adoptSession,
    checking: checkingSession,
  } = useQuickviewSession(navigate);
  const {
    payload,
    snapshot,
    offline,
    loading,
    refreshing,
    lastReloadAt,
    refresh,
  } = usePublicPayload();
  const notifications = useOptionalNotification();
  const isCaptain = session?.user?.role === "captain";
  const canEdit =
    session?.user?.role === "editor" || session?.user?.role === "captain";
  const live = useLiveSession("quickview", Boolean(session?.authenticated));
  // Nur Nicht-Captains werden per Live-Push gesteuert — der Captain ist
  // selbst die Quelle der Auswahl und würde sonst seine eigene Push-Aktion
  // sofort wieder überschrieben bekommen.
  const liveModeActive =
    !isCaptain && live.connected && Boolean(live.liveState?.captainOnline);
  const [dismissedAmbiguitySignature, setDismissedAmbiguitySignature] =
    useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const saved = useMemo(() => readQuickviewSelection(), []);
  const [mapId, setMapId] = useState(saved.mapId ?? "");
  const [side, setSide] = useState<"T" | "CT">(saved.side ?? "T");
  const [tacticId, setTacticId] = useState(saved.tacticId ?? "");
  const [player, setPlayer] = useState<(typeof slots)[number]>(
    saved.player ?? "P1",
  );
  const [openUtility, setOpenUtility] = useState<Utility | null>(null);
  // Auf schmalen Viewports startet die Legende eingeklappt: aufgeklappt
  // verdeckt sie dort einen guten Teil der Karte, und die Karte ist genau das,
  // wofür man die Quickview auf dem Handy öffnet.
  const [legendOpen, setLegendOpen] = useState(() => false);
  const [exporting, setExporting] = useState(false);
  const [relativeNow, setRelativeNow] = useState(() => Date.now());
  const overlayRef = useRef<Konva.Stage | null>(null);
  const revisionBanner = useRevisionBanner(snapshot);
  useEffect(() => {
    const timer = window.setInterval(() => setRelativeNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  async function signOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      const token = sessionRef.current?.csrfToken;
      if (token) await logout(token);
    } finally {
      adoptSession(null);
      void navigate("/editor/login?redirectTo=%2F", { replace: true });
    }
  }
  const activeMaps = payload
    ? payload.maps.filter(
        (map) =>
          map.availability === "active" &&
          payload.tactics.some((tactic) => tactic.mapId === map.id),
      )
    : [];
  const map = findOrFirst(activeMaps, (item) => item.id === mapId);
  const sideTactics = payload
    ? payload.tactics.filter(
        (tactic) => tactic.mapId === map?.id && tactic.side === side,
      )
    : [];
  const tactic =
    findOrFirst(sideTactics, (item) => item.id === tacticId) ??
    payload?.tactics.find((item) => item.mapId === map?.id);
  const assignment = tactic
    ? resolveAssignment(tactic.assignments, player)
    : undefined;
  const playbackPlan = useMemo(
    () => (assignment ? buildPlayback(assignment) : []),
    [assignment],
  );
  const utilityById = new Map(
    (payload?.utilities ?? []).map((utility) => [utility.id, utility]),
  );
  const assignedUtilities = assignment
    ? resolveAssignedUtilities(assignment, tactic!, utilityById)
    : [];
  const animatedUtilityStepOrders = useMemo(() => {
    if (!assignment) return new Set<number>();
    const utilityIds = new Set([
      ...assignment.utilityIds,
      ...(tactic?.teamUtilityIds ?? []),
    ]);
    return new Set(
      (payload?.utilities ?? [])
        .filter(
          (utility) => utilityIds.has(utility.id) && !isPlacedUtility(utility),
        )
        .map((utility) => assignment.utilityStepLinks[utility.id])
        .filter((order): order is number => typeof order === "number"),
    );
  }, [assignment, payload?.utilities, tactic?.teamUtilityIds]);
  const categories = [...new Set(sideTactics.map((item) => item.category))];
  const availableSides = new Set(
    payload && map
      ? payload.tactics
          .filter((item) => item.mapId === map.id)
          .map((item) => item.side)
      : [],
  );

  const {
    selectedStepOrder,
    setSelectedStepOrder,
    walkingStepOrder,
    utilityStepOrder,
    playing,
    setPlaying,
  } = usePlayback({
    plan: playbackPlan,
    animatedUtilitySteps: animatedUtilityStepOrders,
    resetKey: `${tactic?.id ?? ""}:${player}`,
  });

  const playingStepDurationMs =
    playbackPlan.find((step) => step.order === selectedStepOrder)?.durationMs ??
    null;

  useEffect(() => {
    if (!map || !tactic) return;
    localStorage.setItem(
      storageKey,
      JSON.stringify({ mapId: map.id, side, tacticId: tactic.id, player }),
    );
  }, [map?.id, player, side, tactic?.id]);

  useLiveFollow({
    isCaptain,
    connected: live.connected,
    liveState: live.liveState,
    liveModeActive,
    session,
    payload,
    tacticId: tactic?.id,
    player,
    selectTactic: live.selectTactic,
    showTactic: (target) => {
      setMapId(target.mapId);
      setSide(target.side);
      setTacticId(target.id);
    },
    setPlayer,
    notify: (message) => notifications?.notify({ message, kind: "info" }),
  });

  async function exportImage() {
    if (!overlayRef.current || !map || !tactic) return;
    setExporting(true);
    try {
      await exportTacticImage({
        stage: overlayRef.current,
        fileName: `${map.id}-${tactic.name.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-")}-${player.toLowerCase()}.png`,
      });
    } finally {
      setExporting(false);
    }
  }

  function chooseMap(nextMapId: string) {
    if (!payload) return;
    const next =
      payload.tactics.find(
        (item) => item.mapId === nextMapId && item.side === side,
      ) ?? payload.tactics.find((item) => item.mapId === nextMapId);
    if (next) {
      setMapId(nextMapId);
      setSide(next.side);
      setTacticId(next.id);
    }
  }
  function chooseSide(nextSide: "T" | "CT") {
    if (!payload || !map) return;
    const next = payload.tactics.find(
      (item) => item.mapId === map.id && item.side === nextSide,
    );
    if (next) {
      setSide(nextSide);
      setTacticId(next.id);
    }
  }
  if (checkingSession || loading) {
    return (
      <QuickviewStatus session={session} icon={CloudFog} message="LÄDT …" />
    );
  }
  if (!payload) {
    return offline ? (
      <QuickviewStatus
        session={session}
        icon={CloudOff}
        message="Keine Verbindung zum Server und keine lokal gespeicherten Daten vorhanden."
      />
    ) : (
      <QuickviewStatus
        session={session}
        icon={Flag}
        message="Es wurde noch keine Taktik veröffentlicht."
      />
    );
  }
  if (!map) {
    return (
      <QuickviewStatus
        session={session}
        icon={Flag}
        message="Es wurde noch keine Karte veröffentlicht."
      />
    );
  }
  const headingContext = [
    map.name,
    tactic?.category,
    `${side}-Seite`,
    tactic?.buyType,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <AppShell
      active="quickview"
      session={session}
      onSnapshots={canEdit ? () => setSnapshotsOpen(true) : undefined}
      onMembers={() => setMembersOpen(true)}
      online={live.online}
      presenceConnected={live.connected}
      onSignOut={() => void signOut()}
      signingOut={signingOut}
    >
      <main
        className="qv-shell"
        style={
          {
            "--side": side === "T" ? "var(--amber)" : "var(--ct)",
          } as React.CSSProperties
        }
      >
        <header className="qv-page-heading">
          <h1 title={tactic?.name ?? map.name}>{tactic?.name ?? map.name}</h1>
          <p title={headingContext}>{headingContext}</p>
          <span className={offline ? "qv-local" : "qv-published"}>
            {offline ? <CloudOff /> : <Shield />}
            {offline ? "LOKALE VORSCHAU" : "Veröffentlicht"}
          </span>
          {session?.authenticated ? (
            <span
              className={live.connected ? "qv-live" : "qv-live qv-live-off"}
            >
              <Radio />
              {live.connected
                ? live.liveState?.captainOnline
                  ? `Live${isCaptain ? " · Du steuerst" : ""}`
                  : "Verbunden · kein Captain live"
                : "Nicht verbunden"}
            </span>
          ) : null}
          <span className="qv-check">
            <History />
            <span className="sr-only">DATENSTATUS</span>
            {snapshot
              ? `Revision ${snapshot.revision} · ${lastReloadAt === null ? "Gerade eben" : formatRelativeUpdate(lastReloadAt, relativeNow)}`
              : "Ausstehend"}
          </span>
          {revisionBanner.hasUnseenRevision ? (
            <button className="qv-fresh" onClick={revisionBanner.acknowledge}>
              <Sparkles />
              Neue Version · Rev {snapshot?.revision}
            </button>
          ) : null}
        </header>
        <div className="qv-context">
          <label>
            <span className="sr-only">Map auswählen</span>
            <select
              id="qv-map"
              value={map.id}
              disabled={liveModeActive}
              onChange={(event) => chooseMap(event.target.value)}
            >
              {activeMaps.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <ChevronDown />
          </label>
          <div className="qv-side-switch">
            {(["T", "CT"] as const).map((value) => (
              <button
                key={value}
                className={side === value ? "active" : ""}
                disabled={!availableSides.has(value) || liveModeActive}
                onClick={() => chooseSide(value)}
              >
                {value}-Seite
              </button>
            ))}
          </div>
          <label>
            <span className="sr-only">Kategorie auswählen</span>
            <select
              value={tactic?.category ?? ""}
              disabled={liveModeActive || !tactic}
              onChange={(event) =>
                setTacticId(
                  sideTactics.find(
                    (item) => item.category === event.target.value,
                  )?.id ??
                    tactic?.id ??
                    "",
                )
              }
            >
              {categories.map((category) => (
                <option key={category}>{category}</option>
              ))}
            </select>
            <ChevronDown />
          </label>
          <label className="qv-tactic-select">
            <span className="sr-only">Taktik auswählen</span>
            <select
              value={tactic?.id ?? ""}
              disabled={liveModeActive || !tactic}
              onChange={(event) => setTacticId(event.target.value)}
            >
              {sideTactics.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <ChevronDown />
          </label>
          <button
            type="button"
            className="qv-refresh"
            aria-label="Quickview aktualisieren"
            disabled={refreshing}
            onClick={refresh}
          >
            <RefreshCw className={refreshing ? "is-spinning" : undefined} />
            <span>Aktualisieren</span>
          </button>
        </div>
        {tactic && assignment ? (
          <>
            <section className="qv-canvas-card">
              <MapView
                map={map}
                tactic={tactic}
                assignment={assignment}
                utilities={assignedUtilities}
                player={player}
                legendOpen={legendOpen}
                onToggleLegend={() => setLegendOpen((open) => !open)}
                selectedStepOrder={selectedStepOrder}
                playingStepOrder={playing ? walkingStepOrder : null}
                playingStepDurationMs={playing ? playingStepDurationMs : null}
                utilityStepOrder={playing ? utilityStepOrder : null}
                stageRef={overlayRef}
              />
              <nav className="qv-player-tabs" aria-label="Spielerrollen">
                {slots.map((slot) => (
                  <button
                    key={slot}
                    className={player === slot ? "active" : ""}
                    aria-pressed={player === slot}
                    onClick={() => {
                      setPlayer(slot);
                      setSelectedStepOrder(null);
                    }}
                  >
                    <span>{slot.slice(1)}</span>
                    <span>
                      <strong>{slot}</strong>
                      <small>
                        {tactic.assignments.find((item) => item.slot === slot)
                          ?.role ?? ""}
                      </small>
                    </span>
                  </button>
                ))}
              </nav>
              <div className="qv-playback">
                <button
                  onClick={() => setPlaying(!playing)}
                  aria-label={
                    playing ? "Wiedergabe anhalten" : "Schritte abspielen"
                  }
                >
                  {playing ? <Pause /> : <Play />}
                  {playing ? "Anhalten" : "Abspielen"}
                </button>
                <span>
                  {selectedStepOrder === null
                    ? "Alle Schritte"
                    : `Schritt ${selectedStepOrder}`}{" "}
                  · {assignment.steps.length} Schritte
                </span>
                <button
                  aria-label="Als Bild exportieren"
                  disabled={exporting}
                  onClick={() => void exportImage()}
                >
                  <Download />
                </button>
              </div>
            </section>
            <QuickviewDetail
              tactic={tactic}
              map={map}
              assignment={assignment}
              player={player}
              assignedUtilities={assignedUtilities}
              selectedStepOrder={selectedStepOrder}
              setSelectedStepOrder={setSelectedStepOrder}
              setOpenUtility={setOpenUtility}
            />
          </>
        ) : (
          <div className="qv-empty-main">
            <Flag aria-hidden="true" />
            <p>
              Für {map.name.toUpperCase()} ({side}) wurde noch keine Taktik
              veröffentlicht.
            </p>
          </div>
        )}
        {snapshotsOpen ? (
          <SnapshotsDialog
            csrfToken={session?.csrfToken ?? null}
            canRollback={isCaptain}
            onClose={() => setSnapshotsOpen(false)}
          />
        ) : null}
        {openUtility ? (
          <UtilityDetailDialog
            utility={openUtility}
            fallbackRadarPath={map.radarPath}
            onClose={() => setOpenUtility(null)}
          />
        ) : null}
        {membersOpen ? (
          <MembersDialog
            csrfToken={session?.csrfToken ?? null}
            currentUserId={session?.user?.id ?? null}
            canManage={isCaptain}
            online={live.online}
            presenceConnected={live.connected}
            onClose={() => setMembersOpen(false)}
            onAccessLost={() => {
              setMembersOpen(false);
              void getSession().then(adoptSession);
              notifications?.notify({
                message: "DU BIST NICHT MEHR CAPTAIN",
                kind: "success",
              });
            }}
          />
        ) : null}
        {isCaptain &&
        live.ambiguity.length > 0 &&
        JSON.stringify(live.ambiguity) !== dismissedAmbiguitySignature ? (
          <AmbiguityPanel
            groups={live.ambiguity}
            onResolve={(assignments) => live.resolveMatching(assignments)}
            onDismiss={() =>
              setDismissedAmbiguitySignature(JSON.stringify(live.ambiguity))
            }
          />
        ) : null}
      </main>
    </AppShell>
  );
}
